import apiService from './api';
import { getErrorMessage, showErrorToast, showSuccessToast, showErrorDialog } from '../utils';
import { getRefreshTimer, isTokenExpired } from '../utils/jwtUtils';

class SuperAdminAuthService {
  constructor() {
    this.tokenKey = 'super_admin_token';
    this.userKey = 'super_admin_data';
    this.refreshTokenKey = 'super_admin_refresh_token';
    this.refreshTimer = null;
    this.broadcastChannel = null;
    this.isRefreshing = false;
    this.visibilityHandler = null;
    this.setupBroadcastChannel();
  }

  setupBroadcastChannel() {
    if (typeof BroadcastChannel !== 'undefined') {
      this.broadcastChannel = new BroadcastChannel('super_admin_token_refresh');
      
      this.broadcastChannel.onmessage = (event) => {
        if (event.data.type === 'TOKEN_REFRESHED') {
          const { accessToken, refreshToken } = event.data;
          if (accessToken) {
            localStorage.setItem(this.tokenKey, accessToken);
          }
          if (refreshToken) {
            localStorage.setItem(this.refreshTokenKey, refreshToken);
          }
          this.startAutoRefresh();
        }
      };
    }
  }

  startAutoRefresh() {
    this.stopAutoRefresh();

    const token = this.getToken();
    if (!token || isTokenExpired(token)) {
      return;
    }

    const refreshTime = getRefreshTimer(token, 5 * 60 * 1000);
    
    if (!refreshTime || refreshTime <= 0) {
      this.attemptRefresh();
      return;
    }

    this.refreshTimer = setTimeout(() => {
      this.attemptRefresh();
    }, refreshTime);

    if (typeof document !== 'undefined') {
      if (this.visibilityHandler) {
        document.removeEventListener('visibilitychange', this.visibilityHandler);
      }

      this.visibilityHandler = () => {
        if (document.hidden) {
          this.stopAutoRefresh();
        } else {
          this.startAutoRefresh();
        }
      };

      document.addEventListener('visibilitychange', this.visibilityHandler);
    }
  }

  stopAutoRefresh() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    if (this.visibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
  }

  async attemptRefresh() {
    if (this.isRefreshing) {
      return;
    }

    this.isRefreshing = true;

    try {
      const refreshToken = localStorage.getItem(this.refreshTokenKey);
      if (!refreshToken) {
        this.logout();
        return;
      }

      const response = await apiService.post('/super-admin/auth/refresh', {
        refreshToken
      });

      if (response.success && response.data) {
        const { accessToken, refreshToken: newRefreshToken } = response.data;

        localStorage.setItem(this.tokenKey, accessToken);
        if (newRefreshToken) {
          localStorage.setItem(this.refreshTokenKey, newRefreshToken);
        }

        if (this.broadcastChannel) {
          this.broadcastChannel.postMessage({
            type: 'TOKEN_REFRESHED',
            accessToken,
            refreshToken: newRefreshToken
          });
        }

        this.startAutoRefresh();
      } else {
        this.logout();
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      this.logout();
    } finally {
      this.isRefreshing = false;
    }
  }

  async login(credentials) {
    try {
      const response = await apiService.post('/super-admin/auth/login', credentials);

      if (response.success && response.data) {
        const { accessToken, refreshToken, user } = response.data;

        const mergedUser = {
          ...user,
          permissions: response.data.permissions || {},
          fullName: user.fullName || user.username,
          displayName: user.fullName || user.username,
          name: user.fullName || user.username
        };

        localStorage.setItem(this.tokenKey, accessToken);
        localStorage.setItem(this.refreshTokenKey, refreshToken);
        localStorage.setItem(this.userKey, JSON.stringify(mergedUser));

        this.startAutoRefresh();

        showSuccessToast('Welcome! Login successful.');

        return { token: accessToken, user: mergedUser };
      } else {
        const errorMessage = getErrorMessage(response.message || 'Login failed');
        this.showLoginErrorDialog(errorMessage);
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Super admin login error:', error);
      
      let errorMessage = 'Login failed. Please try again.';
      
      if (error.message) {
        errorMessage = getErrorMessage(error.message);
      } else if (error.response?.data?.message) {
        errorMessage = getErrorMessage(error.response.data.message);
      } else if (error.response?.status === 401) {
        errorMessage = 'Invalid username or password';
      } else if (error.response?.status === 429) {
        errorMessage = 'Too many login attempts. Please try again later.';
      }

      this.showLoginErrorDialog(errorMessage);
      throw new Error(errorMessage);
    }
  }

  showLoginErrorDialog(errorMessage) {
    let errorTitle = 'Login Failed';
    const lowerMessage = errorMessage.toLowerCase();
    
    if (lowerMessage.includes('username') || lowerMessage.includes('password') || 
        lowerMessage.includes('incorrect') || lowerMessage.includes('invalid credentials') ||
        lowerMessage.includes('invalid username') || lowerMessage.includes('invalid password')) {
      errorTitle = 'Invalid Credentials';
    } else if (lowerMessage.includes('deactivated') || lowerMessage.includes('disabled')) {
      errorTitle = 'Account Deactivated';
    } else if (lowerMessage.includes('required') || lowerMessage.includes('missing')) {
      errorTitle = 'Missing Information';
    } else if (lowerMessage.includes('not found') || lowerMessage.includes('no account')) {
      errorTitle = 'Invalid Credentials';
    } else if (lowerMessage.includes('too many') || lowerMessage.includes('rate limit')) {
      errorTitle = 'Too Many Attempts';
    }
    
    showErrorDialog(errorMessage, errorTitle, 'OK');
  }

  logout() {
    const token = this.getToken();
    
    if (token) {
      apiService.post('/super-admin/auth/logout', {
        refreshToken: localStorage.getItem(this.refreshTokenKey)
      }).catch(error => {
        console.error('Logout error:', error);
      });
    }

    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.refreshTokenKey);
    localStorage.removeItem(this.userKey);
    this.stopAutoRefresh();

    if (this.broadcastChannel) {
      this.broadcastChannel.close();
      this.broadcastChannel = null;
    }
  }

  getToken() {
    return localStorage.getItem(this.tokenKey);
  }

  getUser() {
    const userData = localStorage.getItem(this.userKey);
    if (!userData) {
      return null;
    }
    try {
      return JSON.parse(userData);
    } catch (error) {
      console.error('Error parsing super admin user data:', error);
      return null;
    }
  }

  isAuthenticated() {
    const token = this.getToken();
    if (!token) {
      return false;
    }
    return !isTokenExpired(token);
  }
}

const superAdminAuthService = new SuperAdminAuthService();

export default superAdminAuthService;

